import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { ProductionCard, ProductionCardSearchCommand } from '../../models/production/production-card';
import { Roll } from '../../models/production/roll';
import { ProductionCardSearchCommandPaginated } from '../../models/production/production-card-search';

@Injectable({
  providedIn: 'root',
})
export class ProductionCardsProxyService {
  private readonly httpClient = inject(HttpClient);
  private _baseUrl = `${environment.proxyUrls.productionApi}/api/v1/production-card`;

  public search(command: ProductionCardSearchCommand, pageNumber?: number, pageSize?: number): Observable<ApiResponse<ProductionCard[]>> {
    let params = new HttpParams();
    if (pageNumber != null) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize != null) params = params.set('pageSize', pageSize.toString());

    return this.httpClient.post<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/search`, command, { params });
  }

  public searchPaginated(
    command: ProductionCardSearchCommand | ProductionCardSearchCommandPaginated,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<ProductionCard>>> {
    let url = `${this._baseUrl}/search/paginated?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ProductionCard>>>(url, command);
  }

  //#region Important Methods
  public getFinishedGoodsCandidateRolls(productionCardId: string, fromLocationCode?: string): Observable<ApiResponse<Roll[]>> {
    let url = `${this._baseUrl}/${productionCardId}/finished-good-candidate-rolls`;
    if (fromLocationCode) {
      url += `?fromLocationCode=${encodeURIComponent(fromLocationCode)}`;
    }
    return this.httpClient.get<ApiResponse<Roll[]>>(url);
  }

  public getByProductionCardNumber(productionCardNumber: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.get<ApiResponse<ProductionCard>>(`${this._baseUrl}/number/${encodeURIComponent(productionCardNumber)}`);
  }

  public getById(productionCardId: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.get<ApiResponse<ProductionCard>>(`${this._baseUrl}/${productionCardId}`);
  }

  public changeStatus(productionCardId: string, newStatus: number): Observable<ApiResponse<null>> {
    return this.httpClient.patch<ApiResponse<null>>(`${this._baseUrl}/${productionCardId}/status`, { newStatus: newStatus });
  }

  public exportToExcel(searchCommand: ProductionCardSearchCommandPaginated): Observable<Blob> {
    return this.httpClient.post(`${this._baseUrl}/search/export`, searchCommand, {
      responseType: 'blob',
    });
  }
}
